import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import {
  Alert,
  Row,
  Button,
  TextInput,
  Select,
  ResponsiveFlex,
  Tooltip,
} from "@narmi/design_system";
import { entities, modules } from "byzantine";
import { importResponseToBulkPayments } from "byzantine/src/dbbl/businessLogic/entities/achImport";
import {
  useMappedEntryDescription,
  MAX_MEMO_LENGTH,
} from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import {
  MAX_BULK_TITLE_LENGTH,
  bulkPaymentSchema,
} from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import Account from "byzantine/src/Account";
import * as formAdapters from "../../../src/adapters";
import ComboAccountSelector from "../../ComboAccountSelector";
import AccountContext from "../../contexts/AccountContext";
import Container from "../../Container";
import HeaderWithSteps from "../../HeaderWithSteps";
import { PAYMENT_ACTIVITY_ROUTE } from "../routes";
import { useACHImportContext } from "../ACHImportContext";
import SECModal from "../../transfer/ach/SECDialog";
import ACHCompanyMissingAlert from "../../transfer/ach/ACHCompanyMissingAlert";

const AdditionalDetailsScreen = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { accounts } = useContext(AccountContext);
  const { nachaResponse, importType } = useACHImportContext();
  const form = entities.bulkPayments.BulkPaymentForm.useForm();
  const { submitForm, setValidationSchema } = form;
  const codes = modules.achPayments.useSECCodes(
    nachaResponse?.rows.map((row) =>
      row.transaction_type === "collect" ? "debit" : "credit"
    ) ?? []
  );
  const { mappedOptions } = useMappedEntryDescription();
  const [showSECDialog, setShowSECDialog] = useState(false);

  const validAccounts = accounts.filter((account: Account) =>
    entities.bulkPayments.isValidBulkAchAccount(
      account,
      nachaResponse?.rows.map((row) => row.transaction_type) ?? []
    )
  );

  const { isACHCompanyMissing, loading: isACHCompanyPending } =
    entities.achCompanies.useACHCompanyForAccount(form.values.fromAccount);
  const fromAccountProps = formAdapters.select(form, "fromAccount");
  const achCompanyFieldError = isACHCompanyMissing
    ? l10n.getString("ach-company-missing-field-error")
    : undefined;

  const toggleSECDialog = () => {
    setShowSECDialog(!showSECDialog);
  };

  // Validate only top-level fields (not payments array)
  useEffect(() => {
    setValidationSchema(bulkPaymentSchema);
  }, [setValidationSchema]);

  // Redirect if no import data
  useEffect(() => {
    if (!nachaResponse) {
      navigate(`/${PAYMENT_ACTIVITY_ROUTE}/upload`, { replace: true });
    }
  }, [nachaResponse, navigate]);

  if (!nachaResponse) {
    return null;
  }

  const handleBack = () => {
    // Navigate to the explicit previous step rather than relying on browser
    // history (navigate(-1)), which loops because the user can reach this
    // screen via the "Back" button on the downstream payments table.
    const hasNewRecipients = nachaResponse.rows.some(
      (row) => row.non_recipient_destination !== null
    );
    if (hasNewRecipients) {
      navigate("../review-recipients");
    } else if (importType === "csv") {
      navigate("../map-columns");
    } else {
      // NACHA with no new recipients comes straight from the file upload step.
      navigate("..");
    }
  };

  const handleNext = async () => {
    // the selected account has no ACH company, so the payment cannot be
    // originated — keep the user on this step to pick another account.
    // while the lookup is still pending we don't yet know, so don't advance
    if (isACHCompanyMissing || isACHCompanyPending) return;

    const { success } = await submitForm();
    if (!success) return;

    form.setFieldValue(
      "payments",
      importResponseToBulkPayments(nachaResponse, importType)
    );

    navigate(`/${PAYMENT_ACTIVITY_ROUTE}/bulk/new/payments`);
  };

  return (
    <Container maxWidth="640px">
      <HeaderWithSteps
        useDisplayFont
        headerText={l10n.getString("file-upload-screen-header")}
      />
      {isACHCompanyMissing ? (
        <div className="margin--top--l">
          <ACHCompanyMissingAlert isActive />
        </div>
      ) : null}
      <div className="margin--top--l">
        <h4 className="fontSize--l nds-sans margin--bottom--xxs">
          {l10n.getString("ach-payment-payment-details-title")}
        </h4>
        <p className="margin--bottom--l">
          {l10n.getString("ach-import-payment-details-description")}
        </p>
        <ResponsiveFlex gapSize="l">
          <Row>
            <Row.Item>
              <TextInput
                label={l10n.getString("bulk-payment-title")}
                maxLength={MAX_BULK_TITLE_LENGTH}
                endContent={
                  <Tooltip
                    text={l10n.getString("bulk-payment-title-tooltip-copy")}
                    maxWidth="235px"
                  >
                    <span className="narmi-icon-info"></span>
                  </Tooltip>
                }
                {...formAdapters.input(form, "title")}
              />
            </Row.Item>
          </Row>

          <div className="margin--bottom--l">
            <Row>
              <Row.Item>
                {validAccounts.length === 0 ? (
                  <>
                    <div className="margin--bottom--s">
                      <Alert isActive kind="warn" isDismissable={false}>
                        {l10n.getString(
                          "bulk-payment-no-valid-source-account-alert"
                        )}
                      </Alert>
                    </div>
                    <TextInput
                      label={l10n.getString("label-account")}
                      disabled
                    />
                  </>
                ) : (
                  <ComboAccountSelector
                    label={l10n.getString("label-account")}
                    accounts={validAccounts}
                    isDestination={true}
                    showAddExternalAccountLink={false}
                    {...fromAccountProps}
                    error={achCompanyFieldError || fromAccountProps.error}
                    errorText={
                      achCompanyFieldError || fromAccountProps.errorText
                    }
                  />
                )}
              </Row.Item>
            </Row>
          </div>

          <Row>
            <Row.Item>
              <Select
                label={l10n.getString("label-transaction-type")}
                disabled={codes.length === 0}
                {...formAdapters.select(form, "secCode")}
              >
                {codes.map((code, index) => (
                  <Select.Item key={`sec_code_${index}`} value={code}>
                    {code}
                  </Select.Item>
                ))}
              </Select>
              <div>
                <Button
                  label={l10n.getString(
                    "ach-payment-learn-more-transaction-types-cta"
                  )}
                  kind="plain"
                  size="xs"
                  testId="bulk-view-transaction-types"
                  onClick={toggleSECDialog}
                />
              </div>
            </Row.Item>
          </Row>

          <div className="margin--bottom--l">
            <Row>
              <Row.Item>
                <Select
                  label={l10n.getString("label-entry-description")}
                  disabled={mappedOptions.length === 0}
                  {...formAdapters.select(form, "entryDesc")}
                >
                  {mappedOptions.map(({ display, value }) => (
                    <Select.Item key={value} value={value}>
                      {display}
                    </Select.Item>
                  ))}
                </Select>
              </Row.Item>
            </Row>
          </div>

          <Row>
            <Row.Item>
              <TextInput
                label={l10n.getString("label-internal-note-optional")}
                maxLength={MAX_MEMO_LENGTH}
                {...formAdapters.input(form, "description")}
              />
            </Row.Item>
          </Row>
        </ResponsiveFlex>
      </div>
      <div className="margin--bottom--xl margin--top--xl">
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button
              kind="negative"
              label={l10n.getString("recipient-review-back")}
              onClick={handleBack}
            />
          </Row.Item>
          <Row.Item shrink>
            <Button
              label={l10n.getString("button-next")}
              onClick={handleNext}
              isLoading={isACHCompanyPending}
            />
          </Row.Item>
        </Row>
      </div>
      <SECModal
        isOpen={showSECDialog}
        handleClose={toggleSECDialog}
        codes={codes}
      />
    </Container>
  );
};

export default AdditionalDetailsScreen;
